/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { DynamicsPrivacyLog } from './dynamics-privacy-log';

/**
 * Wraps the given data in an 'AccessControl' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asAccessControl<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'AccessControl',
        isCustomerData: false,
        isRestrictedData: true,
        isPersonalData: false,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'CustomerContent' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asCustomerContent<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'CustomerContent',
        isCustomerData: true,
        isRestrictedData: true,
        isPersonalData: true,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'EUII' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asEUII<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'EUII',
        isCustomerData: true,
        isRestrictedData: true,
        isPersonalData: true,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'SupportData' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asSupportData<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'SupportData',
        isCustomerData: false,
        isRestrictedData: false,
        isPersonalData: true,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'AccountData' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asAccountData<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'AccountData',
        isCustomerData: false,
        isRestrictedData: false,
        isPersonalData: true,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'PublicPersonalData' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asPublicPersonalData<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'PublicPersonalData',
        isCustomerData: false,
        isRestrictedData: false,
        isPersonalData: true,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'EUPI' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asEUPI<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'EUPI',
        isCustomerData: false,
        isRestrictedData: false,
        isPersonalData: true,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'OII' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asOII<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'OII',
        isCustomerData: false,
        isRestrictedData: false,
        isPersonalData: false,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'SystemMetadata' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asSystemMetadata<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'SystemMetadata',
        isCustomerData: false,
        isRestrictedData: false,
        isPersonalData: false,
        __isDynamicsPrivacy: true
    };
}

/**
 * Wraps the given data in an 'PublicNonPersonalData' DynamicsPrivacyLog object.
 * @param data Data to wrap with privacy information
 */
export function asPublicNonPersonalData<T>(data: T): DynamicsPrivacyLog<T> {
    return {
        data: data,
        classificationLevel: 'PublicNonPersonalData',
        isCustomerData: false,
        isRestrictedData: false,
        isPersonalData: false,
        __isDynamicsPrivacy: true
    };
}

/**
 * Gets the data out of the input object if it is a DynamicsPrivacyLog object.
 * Filters data if it contains privacy information.
 * @param input Input object that may or may not be a DynamicsPrivacyLog
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getFilteredData(input: any): any {
    // Check if the input is a DynamicsPrivacyLog object
    if (input && input.__isDynamicsPrivacy) {
        // If input contains privacy data, censor output
        if (input.isCustomerData || input.isRestrictedData || input.isPersonalData) {
            return `DATA CENSORED (${input.classificationLevel})`;
        } else {
            return input.data;
        }
    } else {
        return input;
    }
}
